import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type OrderStatus =
  | 'pending'
  | 'processing'
  | 'confirmed'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'reservation_pending'
  | 'reservation_approved'
  | 'reservation_rejected'
  | 'reservation_timeout'

export interface Order {
  id: string // externalId
  orderId?: number // API order ID
  email: string
  status: OrderStatus
  createdAt: string
  updatedAt: string
  items: OrderItem[]
  deliveryAddress: Address
  invoiceAddress: Address
  totalPrice: number
  trackingCode?: string
  shipmentDate?: string
  reservationId?: string
  reservationPin?: string
}

export interface OrderItem {
  productId: string
  productName: string
  price: number
  quantity: number
  customization?: Record<string, unknown>
  variantId?: number
}

export interface Address {
  firstName: string
  lastName: string
  street: string
  houseNumber: string
  postalCode: string
  city: string
  countryCode: string
}

interface OrderStore {
  orders: Order[]
  addOrder: (order: Order) => void
  updateOrder: (orderId: string, updates: Partial<Order>) => void
  getOrder: (orderId: string) => Order | undefined
  getOrdersByEmail: (email: string) => Order[]
  clearOrders: () => void
}

export const useOrderStore = create<OrderStore>()(
  persist(
    (set, get) => ({
      orders: [],

      addOrder: (order) => {
        set({
          orders: [...get().orders, order],
        })
      },

      updateOrder: (orderId, updates) => {
        set({
          orders: get().orders.map((order) =>
            order.id === orderId
              ? { ...order, ...updates, updatedAt: new Date().toISOString() }
              : order
          ),
        })
      },

      getOrder: (orderId) => {
        return get().orders.find((order) => order.id === orderId)
      },

      getOrdersByEmail: (email) => {
        return get().orders.filter((order) => order.email === email)
      },

      clearOrders: () => {
        set({ orders: [] })
      },
    }),
    {
      name: 'order-storage',
    }
  )
)
